/**
 * THIN-8023. The documentation page keys every call it makes — templates, codes, drafts, docs-due
 * settings — on the discipline tab, and the tab used to be seeded from the signed-in user's own
 * discipline. A non-clinical user (DOR, administrator, scheduler) has `NOT_APPLICABLE` there, which
 * is a discipline value but not a clinical one: no `documentation_settings` row can match it, so the
 * server logged MISSING_DOCS_DUE_CONFIGURATION for a payer the tenant had configured and returned
 * code defaults instead of the configured values. Two ternaries in this folder also coerced it to
 * SLP, so those users saw SLP templates and SLP drafts.
 *
 * These helpers make the clinical set explicit so a non-clinical value can neither be sent nor
 * silently become SLP.
 */

export const CLINICAL_DISCIPLINES = ['PT', 'OT', 'SLP'] as const;

export type ClinicalDiscipline = (typeof CLINICAL_DISCIPLINES)[number];

export const isClinicalDiscipline = (value: unknown): value is ClinicalDiscipline =>
  typeof value === 'string' && (CLINICAL_DISCIPLINES as readonly string[]).includes(value);

/** The clinical discipline behind a tab value, or `null` when the tab is not one (`''`, `NOT_APPLICABLE`). */
export const toClinicalDiscipline = (value: unknown): ClinicalDiscipline | null =>
  isClinicalDiscipline(value) ? value : null;

export type PlanOfCareActivity = {
  checkPtActive?: boolean;
  checkOtActive?: boolean;
  checkSlpActive?: boolean;
};

/**
 * Which discipline tab to open a patient's documentation on.
 *
 * A clinical viewer lands on their own discipline, as before. A non-clinical viewer has no discipline
 * to contribute, so the episode of care decides: its first active discipline in tab order, or PT when
 * none is active. Returns `null` while the plan of care has not loaded, so the caller waits rather
 * than guessing.
 */
export const resolveDefaultDisciplineTab = (
  viewerDiscipline: unknown,
  planOfCare: PlanOfCareActivity | null | undefined
): ClinicalDiscipline | null => {
  if (isClinicalDiscipline(viewerDiscipline)) {
    return viewerDiscipline;
  }
  if (!planOfCare) {
    return null;
  }
  if (planOfCare.checkPtActive) return 'PT';
  if (planOfCare.checkOtActive) return 'OT';
  if (planOfCare.checkSlpActive) return 'SLP';
  return 'PT';
};
